<!--
* Lays out contents item form
*
* @param {ContentItem} currentItem - Current item object if editing
* @param {number} currentItemIndex - Current items index in array
* @param {boolean} addToClaim - Whether to add item to claim
*
* @fires MsContentsItemForm#imageValidityCheck
* @fires MsContentsItemForm#purchaseDateChange
-->

<!--
* Emits image validity check
*
* @event MsContentsItemForm#imageValidityCheck
-->

<!--
* Emits purchase date change
*
* @event MsContentsItemForm#purchaseDateChange
-->

<template>
  <div>
    <!-- Todo add logic to toggle fields based on a dropdown of what contents type -->
    <!-- Damaged item name -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.name`) }}</label>
      <div class="control has-icons-right">
        <input
          v-model="name"
          v-validate="'required'"
          :data-vv-as="$t(`${pagei18nKey}.name`)"
          class="input"
          :class="{'is-danger': errors.has('name') }"
          name="name"
          type="text"
          :placeholder="$t(`${pagei18nKey}.name`)"
          autocomplete="off">
        <span
          v-show="fields.name && fields.name.valid"
          class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
      </div>
      <p
        v-show="errors.has('name')"
        class="help is-danger">
        {{ errors.first('name') }}
      </p>
    </div>

    <!-- Damage Occurred -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.damageDesc`) }}</label>
      <div class="control has-icons-right">
        <input
          v-model="damageDesc"
          v-validate="'required'"
          :data-vv-as="$t(`${pagei18nKey}.damageDesc`)"
          class="input"
          :class="{'is-danger': errors.has('damageDesc') }"
          name="damageDesc"
          type="text"
          :placeholder="$t(`${pagei18nKey}.damageDesc`)"
          autocomplete="off">
        <span
          v-show="fields.damageDesc && fields.damageDesc.valid"
          class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
      </div>
      <p
        v-show="errors.has('damageDesc')"
        class="help is-danger">
        {{ errors.first('damageDesc') }}
      </p>
    </div>

    <!-- Proof of damages -->
    <div class="section_subtitle">{{ $t(`${pagei18nKey}.proofOfDamagesTitle`) }}</div>
    <!-- Image uploads -->
    <div class="field">
      <ms-image-upload-wrapper
        class="image_upload"
        name="wholeCaseUpload"
        :showControls="item.images.wholeItem !== undefined"
        :defaultImageSrc="wholeItemUpload.defaultImageSrc"
        :imageUrl="getWholeItemImage()"
        :caption="wholeItemUpload.caption"
        :uploadFolder="claimId"
        @imageUploadSuccess="onWholeItemUploadSuccess(wholeItemUpload, ...arguments)"
        @imageDeleteClick="onWholeItemUploadDelete"
      />
    </div>

    <div class="field">
      <!-- Area of damage image upload -->
      <ms-image-upload-wrapper
        class="image_upload"
        v-for="(upload, index) in item.areaOfDamageImages"
        :key="`area-of-damage-${index}`"
        :defaultImageSrc="upload.defaultImageSrc"
        :imageUrl="getAreaOfDamageImage(upload)"
        :showControls="currentItem && currentItem.areaOfDamageImages.length > 0"
        :caption="upload.caption"
        :file="upload.file"
        :uploadFolder="claimId"
        :showSelect="upload.showSelect"
        :selectPlaceholder="upload.selectPlaceholder"
        :selectDisplayKey="upload.selectDisplayKey"
        :selectVal.sync="upload.selectVal"
        :selectOptions="upload.selectOptions"
        @imageUploadSuccess="onDamagedAreaUploadSuccess(index, ...arguments)"
        @imageDeleteClick="onAreaOfDamageImageDelete(upload, index)"
      />
    </div>

    <!-- More photos button -->
    <ms-file-input-button
      v-if="moreUploadsButton.show"
      class="more_uploads_button"
      :button="moreUploadsButton"
      @change="onMoreUploadsChange"/>

    <div class="section_subtitle">{{ $t(`${pagei18nKey}.costTitle`) }}</div>
    <ms-price-input
      :selectedCurrency.sync="currency"
      :inputValue.sync="price"
      selectDisplayKey="name"
      :currencies="currencies"
      class="cost_input"
    />

    <!-- Date purchased -->
    <div class="field">
      <label class="label">{{ $t(`${pagei18nKey}.datePurchased`) }}</label>
      <ms-datetime-picker
        v-model="purchaseDate"
        :locale="locale"
        :placeholder="$t('message.datePlaceholderText')"
        :type="datePickerType"
        :maxDatetime="maxDatetime"/>
    </div>

    <!-- Add a receipt button -->
    <ms-file-input-button
      v-if="getReceiptImage() === null && getReceiptFile() === null"
      class="add_a_receipt__button"
      :button="this.addReceiptButton"
      @change="onAddReceiptClick"/>

    <ms-image-upload
      class="image_upload"
      v-if="getReceiptFile() || getReceiptImage()"
      :imageUrl="getReceiptImage()"
      :noImageSrc="''"
      :showControls="true"
      :file="getReceiptFile()"
      :uploadFolder="claimId"
      @uploadSuccess="onReceiptUploadSuccess(...arguments)"
      @deleteClick="onReceiptImageDelete()"/>

    <p class="add_receipt_small_print">{{ $t(`${pagei18nKey}.receiptNote`) }}</p>
  </div>
</template>

<script>
  import api from '@/api/api'
  import AppButton, {ButtonStates, ButtonTypes} from '@/components/app-button'
  import AppSelect from '@/components/app-select'
  import MsDatetimePicker, {DatetimeTypes} from '@/components/ms-datetime-picker'
  import MsPriceInput from '@/components/ms-price-input'
  import MsImageUpload from '@/components/ms-image-upload'
  import MsImageUploadWrapper from '@/components/ms-image-upload-wrapper'
  import MsFileInputButton from '@/components/ms-file-input-button'
  import themeSettings from '../../theme-settings.json'

  export default {
    inject: ['$validator'],
    name: 'ContentsItemForm',
    components: {
      AppButton,
      AppSelect,
      MsDatetimePicker,
      MsPriceInput,
      MsImageUpload,
      MsImageUploadWrapper,
      MsFileInputButton
    },

    props: {
      claimId: String,
      currentItem: Object,
      currentItemIndex: Number,
      addToClaim: {
        type: Boolean,
        default: false
      }
    },

    data () {
      return {
        pagei18nKey: 'message.contentsItem',
        currencies: [],
        imagesRequired: themeSettings.theme.imagesRequired,
        item: {
          name: '',
          purchaseDate: null,
          damageDesc: '',
          images: {},
          areaOfDamageImages: [],
          receipt: null,
          currency: 1,
          currencyString: '',
          price: null
        },
        moreUploadsButton: {
          show: this.currentItem && this.currentItem.areaOfDamageImages !== undefined,
          state: ButtonStates.ENABLED,
          type: ButtonTypes.QUINARY,
          hasIconLeft: true,
          text: 'message.contentsItem.morePhotos'
        },
        wholeItemUpload: {
          caption: this.$t('message.contentsItem.wholeItemUpload'),
          defaultImageSrc: '/static/theme/img/whole-contents-photo.png'
        },
        areaOfDamageUpload: {
          caption: this.$t('message.contentsItem.areaOfDamageUpload.caption'),
          defaultImageSrc: '/static/theme/img/area-of-damage-photo.png',
          selectPlaceholder: this.$t('message.contentsItem.areaOfDamageUpload.selectPlaceholder'),
          selectDisplayKey: 'option',
          selectOptions: [
            {
              option: this.$t('message.contentsItem.areaOfDamageUpload.selectOptions.top')
            },
            {
              option: this.$t('message.contentsItem.areaOfDamageUpload.selectOptions.bottom')
            },
            {
              option: this.$t('message.contentsItem.areaOfDamageUpload.selectOptions.left')
            },
            {
              option: this.$t('message.contentsItem.areaOfDamageUpload.selectOptions.right')
            }
          ],
          selectVal: null,
          showSelect: false
        },
        receiptUpload: {
          imageUrl: null,
          file: null
        }
      }
    },

    beforeMount () {
      if (this.currentItem) {
        for (let prop in this.item) {
          this.item[prop] = this.currentItem[prop]
        }
      }
    },

    mounted () {
      // Push into area of damage uploads array first upload object
      if (!this.currentItem) {
        this.item.areaOfDamageImages.push({ ...this.areaOfDamageUpload })
        this.item.receipt = this.receiptUpload
      } else {
        // We emit purchase date change on mount to see if we have a
        // value (usefully when editting an item)
        this.$emit('purchaseDateChange', this.purchaseDate)
        this.imageValidityCheck()
        this.$emit('priceInputChange', this.price)
      }
      /**
       * Get currencies data from api
       */
      api.getCurrencies()
        .then(response => {
          this.currencies = response.data
          // as currency is pre-selected make sure we grab the string value on load
          this.item.currencyString = this.getStringValueOfObjectFromKey(
            this.item.currency,
            this.currencies,
            'id',
            'currencyCode'
          )
        })
        .catch(console.error)
    },

    computed: {
      /**
       * Get date locale code
       */
      locale: {
        get () {
          const currentLanguage = this.$store.getters.language
          if (
            currentLanguage &&
            currentLanguage.dateLocaleCode
          ) {
            return currentLanguage.dateLocaleCode
          }
          return null
        }
      },

      /**
       * Get datetime type
       */
      datePickerType: {
        get () {
          return DatetimeTypes.YEAR
        }
      },

      /**
       *  Gets max datetime
       */
      maxDatetime: {
        get () {
          return new Date().getTime()
        }
      },

      currency: {
        /**
         * Get current stored currency
         */
        get () {
          return this.getIndexOfObjectFromKey(
            this.item.currency,
            this.currencies,
            'id'
          )
        },

        /**
         * Set currency
         *
         * @param {number} value - Id of currency to set currency to
         */
        set (value) {
          this.item.currency = value.id
          if (value.index !== null) {
            this.item.currencyString = this.getStringValueOfObjectFromKey(
              this.item.currency,
              this.currencies,
              'id',
              'currencyCode'
            )
          }
        }
      },

      price: {
        /**
         * Get current stored price
         */
        get () {
          return this.item.price / 100
        },

        /**
         * Set price
         *
         * @param {string} value - Value to set price to
         */
        set (value) {
          this.$emit('priceInputChange', value)
          this.item.price = parseInt(value * 100)
        }
      },

      /**
       * Get add a receipt button object
       */
      addReceiptButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: ButtonTypes.QUATERNARY,
            text: `${this.pagei18nKey}.receiptButton`,
            hasIconLeft: true
          }
        }
      },

      purchaseDate: {
        /**
         * Get the current item purchase date
         */
        get () {
          return this.item.purchaseDate
        },

        /**
         * Set the item purchase date
         *
         * @param {number} value - Epoch of purchase date
         */
        set (value) {
          this.$emit('purchaseDateChange', value)
          this.item.purchaseDate = value
        }
      },

      name: {
        /**
         * Get current stored content item name
         */
        get () {
          return this.item.name
        },

        /**
         * Set content item name
         *
         * @param {string} value - Value to set content item name to
         */
        set (value) {
          this.item.name = value
        }
      },

      damageDesc: {
        /**
         * Get current stored content item description
         */
        get () {
          return this.item.damageDesc
        },

        /**
         * Set content item description
         *
         * @param {string} value - Value to set damage description to
         */
        set (value) {
          this.item.damageDesc = value
        }
      },

      /**
       * Get new image upload button object
       */
      newImageUploadButton: {
        get () {
          return {
            state: ButtonStates.ENABLED,
            type: ButtonTypes.quaternary
          }
        }
      }
    },

    watch: {
      /**
       * We watch addToClaim prop from parent to trigger
       * add item to claim function once addToClaim is true
       *
       * @param {boolean} newVal - The new value of addToClaim
       */
      addToClaim: function (newVal) {
        if (newVal) {
          this.addItemToClaim()
        }
      }
    },

    methods: {
      /**
       * Remove the image from the whole item upload
       */
      onWholeItemUploadDelete () {
        this.item.images.wholeItem.imageUrl = null
        this.imageValidityCheck()
      },

      /**
       * Get the whole item upload image url
       */
      getWholeItemImage () {
        if (this.item.images.wholeItem) {
          let storedWholeItemImage = this.item.images.wholeItem
          if (storedWholeItemImage && storedWholeItemImage.imageUrl) {
            return storedWholeItemImage.imageUrl
          }
          return null
        }
      },

      /**
       * Add new area of damage uploads for each file in event
       *
       * @param {Event} event - the input file change event
       */
      onMoreUploadsChange (event) {
        let newUploads = []
        const files = Array.from(event.target.files)
        files.forEach((file) => {
          newUploads.push(
            Object.assign(
              JSON.parse(JSON.stringify(this.areaOfDamageUpload)),
              {
                file: file
              }
            )
          )
        })
        this.item.areaOfDamageImages.push(...newUploads)
      },

      // todo logic for validation
      /**
       * on image upload success for individual upload fields
       * push image object into the item's images
       *
       * @param {string} url - Url of uploaded image
       */
      onWholeItemUploadSuccess (upload, url) {
        this.item.images.wholeItem = Object.assign(upload, { 'imageUrl': url })
        this.imageValidityCheck()
      },

      onReceiptUploadSuccess (url) {
        this.item.receipt = Object.assign(this.item.receipt, { 'imageUrl': url })

        // Remove file so, on edit file wouldn't get initialised once again
        this.item.receipt.file = null
      },

      /**
       * On damaged area image upload success push the relevant details
       * into the current upload item to be pushed into images array on submit
       *
       * @param {number} index - Index of upload object in areaOfDamageUploads array
       * @param {string} url - Url of uploaded image
       */
      onDamagedAreaUploadSuccess (index, url) {
        // Show the select options
        this.item.areaOfDamageImages[index].showSelect = true

        // Format the data we need to pass to the store later on
        this.item.areaOfDamageImages[index].imageUrl = url

        // If the more uploads button isn't already showing then show it
        if (!this.moreUploadsButton.show) {
          this.moreUploadsButton.show = true
        }

        // After initial upload, set file to null to avoid additional initiliasation
        this.item.areaOfDamageImages[index].file = null
        this.imageValidityCheck()
      },

      /**
       * On area of damage image delete click, reset uploader field or delete
       * item entirely if it is not the first item
       *
       * @param {Object} upload - The upload object for the deleted image item
       * @param {number} index - The index of the image in areaOfDamageImages array
       */
      onAreaOfDamageImageDelete (upload, index) {
        if (index > 0) {
          this.item.areaOfDamageImages.splice(index, 1)
        } else {
          upload.imageUrl = null
          upload.showSelect = false
          upload.showControls = false
          upload.selectVal = null
        }

        this.moreUploadsButton.show = this.item.areaOfDamageImages[0].imageUrl
        this.imageValidityCheck()
      },

      /**
       * Remove the image from the receipt upload
       */
      onReceiptImageDelete () {
        this.item.receipt.imageUrl = null
        this.item.receipt.file = null
      },

      /**
       * Get an area of damage upload's image url
       *
       * @param {string} upload - The upload object to get image url for
       */
      getAreaOfDamageImage (upload) {
        return upload.imageUrl ? upload.imageUrl : null
      },

      /**
       * Check whether the minimum images are uploaded and fire an emit
       * with valid value
       */
      imageValidityCheck: function () {
        // if images not required then emit event
        if (!this.imagesRequired) {
          this.$emit('imageValidityCheck', true)
          return
        }

        if (
          this.item.images.wholeItem &&
          this.item.areaOfDamageImages[0]
        ) {
          const imageFields = [
            this.item.images.wholeItem.imageUrl,
            this.item.areaOfDamageImages[0].imageUrl
          ]
          if (imageFields.every((image) => image !== undefined && image !== null)) {
            this.$emit('imageValidityCheck', true)
          } else {
            this.$emit('imageValidityCheck', false)
          }
        } else {
          this.$emit('imageValidityCheck', false)
        }
      },

      /**
       * Change item purchase date on change
       */
      onDatePurchasedChange (date) {
        this.item.purchaseDate = date
      },

      /**
       * On add a receipt button
       */
      onAddReceiptClick (event) {
        this.item.receipt = Object.assign(this.receiptUpload, { 'file': event.target.files[0] })
      },

      /**
       * Get receipt's image url
       */
      getReceiptImage () {
        if (this.item.receipt) {
          return this.item.receipt.imageUrl ? this.item.receipt.imageUrl : null
        }
        return null
      },

      /**
       * Get receipt's file
       */
      getReceiptFile () {
        if (this.item.receipt) {
          return this.item.receipt.file ? this.item.receipt.file : null
        }
        return null
      },

      /**
       * Add item to claim
       */
      addItemToClaim () {
        let completedItem
        if (this.currentItem) {
          // Deep clone item
          completedItem = {
            data: this.item,
            index: this.currentItemIndex
          }
          this.$store.commit('replaceContentsItem', completedItem)
        } else {
          this.$store.commit('addContentsItem', this.item)
        }

        this.$router.replace('/claims-items-summary')
      },

      /**
       * Adds new image Upload
       */
      addNewImageUpload () {
        // console.log('image upload clicked')
      },

      /**
       * Get the index of an object that has a matching key
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - The data to check in
       * @param {string} key - The key to check value in each object
       */
      getIndexOfObjectFromKey: function (value, data, key) {
        let foundIndex = data.findIndex(item => {
          return item[key] === value
        })

        return foundIndex === -1 ? value : foundIndex
      },

      /**
       * Get string value based on matching another key value
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - Data to search in
       * @param {string} searchKey - Key to match value in
       * @param {string} stringKey - Key to get string value
       */
      getStringValueOfObjectFromKey: function (value, data, searchKey, stringKey) {
        let foundItem = data.find(item => {
          return item[searchKey] === value
        })

        return foundItem[stringKey]
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../assets/sass/main.scss";

  .section_subtitle {
    @extend .subtitle;
    padding-top: $dims-spacing-large;
    margin-top: $dims-spacing-large;
    border-top: $default-border-style;
  }

  .image_upload {
    margin-bottom: $dims-spacing-small;
  }

  .more_uploads_button {
    ::v-deep .app_button .app_button__icon_left {
      height: $dims-more-photo-uploads-button-icon-size;
      width: $dims-more-photo-uploads-button-icon-size;
    }
  }

  .add_a_receipt__button {
    max-width: none;
    margin: $dims-spacing-xlarge auto $dims-spacing-large auto;
    padding: $dims-spacing-small 0;

    ::v-deep .app_button__icon_left {
      height: $dims-add-receipt-button-icon-size;
      width: $dims-add-receipt-button-icon-size;
    }
  }

  .add_receipt_small_print {
    margin-bottom: $dims-spacing-large;
  }

  .cost_input {
    margin-bottom: $dims-spacing-large;
  }
</style>

